// Tassonomia statica del marketplace + centroidi geografici delle città italiane.
// Le categorie sono raggruppate per UX ma esposte anche come lista piatta (CUISINES)
// per retro-compatibilità con il motore di ricerca esistente.

export type CuisineGroup = {
  label: string;
  items: string[];
};

/** Gruppi ordinati; ciascun gruppo è ordinato alfabeticamente. */
export const CUISINE_GROUPS: CuisineGroup[] = [
  {
    label: "Cucina Italiana",
    items: [
      "Abruzzese", "Calabrese", "Emiliana", "Friulana", "Ligure", "Lucana",
      "Marchigiana", "Napoletana", "Piemontese", "Pugliese", "Romana",
      "Sarda", "Siciliana", "Toscana", "Tradizionale", "Umbra", "Veneta",
    ].sort(),
  },
  {
    label: "Cucine Internazionali",
    items: [
      "Americana", "Argentina", "Brasiliana", "Cinese", "Coreana", "Francese",
      "Giapponese", "Greca", "Indiana", "Israeliana", "Libanese", "Marocchina",
      "Messicana", "Peruviana", "Spagnola", "Thailandese", "Turca", "Vietnamita",
    ].sort(),
  },
  {
    label: "Specialità",
    items: [
      "BBQ", "Crudi", "Dim Sum", "Frutti di mare", "Griglia", "Pasta fresca",
      "Pesce", "Pizza", "Poke", "Ramen", "Steakhouse", "Sushi", "Tartare",
      "Tartufo", "Teppanyaki",
    ].sort(),
  },
  {
    label: "Alimentazione",
    items: [
      "Biologica", "Gluten Free", "Healthy", "Keto", "Paleo",
      "Senza lattosio", "Vegana", "Vegetariana",
    ].sort(),
  },
  {
    label: "Dolci",
    items: ["Cake Design", "Cioccolateria", "Dessert Gourmet", "Gelateria", "Pasticceria"].sort(),
  },
  {
    label: "Beverage",
    items: ["Bartender", "Cocktail Experience", "Mixology", "Sommelier", "Wine Pairing"].sort(),
  },
];

/** Lista piatta (alfabetica, unica) per compatibilità con il vecchio Select. */
export const CUISINES: string[] = Array.from(
  new Set(CUISINE_GROUPS.flatMap((g) => g.items)),
).sort((a, b) => a.localeCompare(b, "it"));

export type Cuisine = string;

/** Numero massimo di specialità che uno chef può selezionare. */
export const MAX_SPECIALTIES = 5;

export const REGIONS = [
  "Abruzzo",
  "Basilicata",
  "Calabria",
  "Campania",
  "Emilia-Romagna",
  "Friuli-Venezia Giulia",
  "Lazio",
  "Liguria",
  "Lombardia",
  "Marche",
  "Molise",
  "Piemonte",
  "Puglia",
  "Sardegna",
  "Sicilia",
  "Toscana",
  "Trentino-Alto Adige",
  "Umbria",
  "Valle d'Aosta",
  "Veneto",
];

// Centroidi delle principali città italiane (lat, lng).
// Copre tutti i capoluoghi di provincia italiani: nessun chef con città valida
// scompare dalla mappa per coordinate mancanti.
const COORDS: Record<string, [number, number]> = {
  milano: [45.4642, 9.19],
  roma: [41.9028, 12.4964],
  napoli: [40.8518, 14.2681],
  torino: [45.0703, 7.6869],
  firenze: [43.7696, 11.2558],
  bologna: [44.4949, 11.3426],
  venezia: [45.4408, 12.3155],
  verona: [45.4384, 10.9916],
  genova: [44.4056, 8.9463],
  palermo: [38.1157, 13.3613],
  catania: [37.5079, 15.083],
  bari: [41.1171, 16.8719],
  cagliari: [39.2238, 9.1217],
  parma: [44.8015, 10.328],
  modena: [44.6471, 10.9252],
  padova: [45.4064, 11.8768],
  trieste: [45.6495, 13.7768],
  brescia: [45.5416, 10.2118],
  bergamo: [45.6983, 9.6773],
  como: [45.8081, 9.0852],
  monza: [45.5845, 9.2744],
  pisa: [43.7228, 10.4017],
  lucca: [43.8429, 10.5027],
  siena: [43.3188, 11.3308],
  perugia: [43.1107, 12.3908],
  rimini: [44.0678, 12.5695],
  trento: [46.0748, 11.1217],
  bolzano: [46.4983, 11.3548],
  ancona: [43.6158, 13.5189],
  pescara: [42.4584, 14.2082],
  taranto: [40.4644, 17.247],
  lecce: [40.3515, 18.1718],
  salerno: [40.6824, 14.7681],
  sorrento: [40.6263, 14.3757],
  amalfi: [40.634, 14.6027],
  alba: [44.7009, 8.0357],
  asti: [44.9009, 8.2068],
  // --- Altri capoluoghi & città principali ---
  aosta: [45.7372, 7.3206],
  novara: [45.4469, 8.6219],
  vercelli: [45.3208, 8.4185],
  biella: [45.5664, 8.0533],
  cuneo: [44.3841, 7.5426],
  varese: [45.8205, 8.8251],
  pavia: [45.1847, 9.1582],
  cremona: [45.1335, 10.0226],
  mantova: [45.1564, 10.7914],
  lecco: [45.8566, 9.3976],
  sondrio: [46.17, 9.8702],
  lodi: [45.314, 9.5036],
  belluno: [46.139, 12.2167],
  treviso: [45.6669, 12.2431],
  vicenza: [45.5455, 11.5354],
  rovigo: [45.0697, 11.7918],
  udine: [46.0626, 13.2345],
  pordenone: [45.9626, 12.6605],
  gorizia: [45.9408, 13.6217],
  "reggio emilia": [44.6989, 10.6297],
  "reggio nell'emilia": [44.6989, 10.6297],
  piacenza: [45.0526, 9.6929],
  "forli": [44.2226, 12.0407],
  cesena: [44.1391, 12.2431],
  ferrara: [44.8381, 11.6198],
  ravenna: [44.4184, 12.2035],
  imperia: [43.8896, 8.0388],
  savona: [44.3094, 8.4774],
  "la spezia": [44.1024, 9.8241],
  sanremo: [43.8154, 7.7763],
  arezzo: [43.4633, 11.8796],
  prato: [43.8809, 11.0972],
  livorno: [43.5485, 10.3106],
  pistoia: [43.9335, 10.9177],
  grosseto: [42.7596, 11.1129],
  "massa": [44.0349, 10.1414],
  carrara: [44.0793, 10.0976],
  viareggio: [43.8665, 10.2502],
  terni: [42.5635, 12.6433],
  "ascoli piceno": [42.8536, 13.5749],
  macerata: [43.3009, 13.4536],
  fermo: [43.1607, 13.7177],
  "pesaro": [43.9102, 12.9132],
  urbino: [43.7262, 12.6365],
  viterbo: [42.4175, 12.1067],
  rieti: [42.4031, 12.864],
  frosinone: [41.6402, 13.3504],
  latina: [41.4677, 12.9037],
  "l'aquila": [42.3498, 13.3995],
  laquila: [42.3498, 13.3995],
  chieti: [42.3514, 14.1679],
  teramo: [42.6589, 13.7044],
  campobasso: [41.5601, 14.6627],
  isernia: [41.5963, 14.2306],
  caserta: [41.0734, 14.3327],
  avellino: [40.9145, 14.7906],
  benevento: [41.13, 14.7826],
  pompei: [40.7484, 14.5007],
  ischia: [40.7384, 13.945],
  capri: [40.5532, 14.2222],
  positano: [40.6281, 14.4847],
  potenza: [40.6395, 15.8051],
  matera: [40.6664, 16.6043],
  cosenza: [39.2982, 16.2536],
  catanzaro: [38.9098, 16.5877],
  "reggio calabria": [38.1109, 15.6469],
  crotone: [39.0808, 17.1273],
  "vibo valentia": [38.6759, 16.1018],
  brindisi: [40.6386, 17.9466],
  "barletta": [41.32, 16.2779],
  andria: [41.2314, 16.2917],
  trani: [41.2768, 16.4179],
  foggia: [41.4622, 15.5446],
  monopoli: [40.9525, 17.302],
  alberobello: [40.7826, 17.2349],
  ostuni: [40.7305, 17.5772],
  gallipoli: [40.0556, 17.9923],
  otranto: [40.1473, 18.4912],
  "messina": [38.1938, 15.5546],
  agrigento: [37.3111, 13.5765],
  trapani: [38.0176, 12.5365],
  siracusa: [37.0755, 15.2866],
  ragusa: [36.9266, 14.7264],
  caltanissetta: [37.4906, 14.0625],
  enna: [37.5658, 14.2725],
  taormina: [37.8516, 15.2853],
  cefalu: [38.0386, 14.0227],
  "cefalù": [38.0386, 14.0227],
  marsala: [37.7986, 12.4344],
  sassari: [40.7259, 8.5557],
  nuoro: [40.3208, 9.3287],
  oristano: [39.9036, 8.5916],
  olbia: [40.9233, 9.4986],
  alghero: [40.5594, 8.3196],
  faenza: [44.286, 11.8836],
  imola: [44.353, 11.7146],
  carpi: [44.7811, 10.8786],
  fano: [43.8434, 13.0186],
  "san benedetto del tronto": [42.949, 13.8804],
  spoleto: [42.7396, 12.7385],
  assisi: [43.0707, 12.6196],
  orvieto: [42.7186, 12.1107],
  cortona: [43.2752, 11.985],
  "san gimignano": [43.4675, 11.0432],
  volterra: [43.4007, 10.8606],
  "monte argentario": [42.4019, 11.1118],
  portofino: [44.3037, 9.2096],
  lerici: [44.0775, 9.9117],
  bormio: [46.4675, 10.3725],
  cortina: [46.5405, 12.1357],
  "cortina d'ampezzo": [46.5405, 12.1357],
  livigno: [46.5384, 10.1359],
  merano: [46.6701, 11.1591],
  bressanone: [46.7176, 11.6566],
  ortisei: [46.5739, 11.6748],
  riva: [45.8853, 10.8398],
  "riva del garda": [45.8853, 10.8398],
  desenzano: [45.4694, 10.535],
  sirmione: [45.4956, 10.6064],
  garda: [45.5778, 10.7077],
  malcesine: [45.7635, 10.81],
  "marina di pietrasanta": [43.9382, 10.227],
  "forte dei marmi": [43.9612, 10.1696],
};

// Centroidi regionali — usati come fallback se la città non è mappata,
// per evitare che uno chef sparisca dalla mappa.
const REGION_COORDS: Record<string, [number, number]> = {
  abruzzo: [42.351, 13.398],
  basilicata: [40.6395, 15.8051],
  calabria: [38.9098, 16.5877],
  campania: [40.8518, 14.2681],
  "emilia-romagna": [44.4949, 11.3426],
  "friuli-venezia giulia": [46.0626, 13.2345],
  lazio: [41.9028, 12.4964],
  liguria: [44.4056, 8.9463],
  lombardia: [45.4642, 9.19],
  marche: [43.6158, 13.5189],
  molise: [41.5601, 14.6627],
  piemonte: [45.0703, 7.6869],
  puglia: [41.1171, 16.8719],
  sardegna: [39.2238, 9.1217],
  sicilia: [38.1157, 13.3613],
  toscana: [43.7696, 11.2558],
  "trentino-alto adige": [46.0748, 11.1217],
  umbria: [43.1107, 12.3908],
  "valle d'aosta": [45.7372, 7.3206],
  veneto: [45.4408, 12.3155],
};

function normalize(s: string): string {
  return s.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

/** Restituisce lat/lng a partire dal nome città italiano (case-insensitive). */
export function cityToCoords(city: string | null | undefined): [number, number] | null {
  if (!city) return null;
  const key = normalize(city);
  return COORDS[key] ?? null;
}

/** Fallback regionale: usato solo quando la città non è mappata, per non perdere lo chef. */
export function regionToCoords(region: string | null | undefined): [number, number] | null {
  if (!region) return null;
  return REGION_COORDS[normalize(region)] ?? null;
}